// Topbar — EVE-Mini-Avatar + State (FOCUSED/Stress) + Sync + Next-Trigger + Bell.
// Source-of-truth: mockups/03-shell/B-bolt-hero.html (header).

import { useMemo } from 'react';
import { Bell } from 'lucide-react';
import { useEntityState } from '@/hooks/use-entity-state';
import { useEveProfile } from '@/hooks/use-eve-profile';
import { useMarketStore } from '@/stores/market-store';
import { formatScheduledCountdown, getNextScheduledEvent } from '@/services/scheduler';
import { MacroHeaderPopup } from '@/components/markets/macro-header-popup';
import { CreditBadge } from '@/components/credits/credit-badge';

export function Topbar() {
  const entity = useEntityState();
  const { profile: eveProfile } = useEveProfile();
  const lastSyncAt = useMarketStore((s) => s.lastSyncAt);

  const nextEvent = useMemo(() => getNextScheduledEvent(eveProfile, new Date()), [eveProfile]);

  const syncLabel = useMemo(() => {
    if (!lastSyncAt) return '—';
    const secs = Math.max(0, Math.round((Date.now() - lastSyncAt) / 1000));
    if (secs < 60) return `${secs}s`;
    return `${Math.round(secs / 60)}m`;
  }, [lastSyncAt]);

  const stress = Math.round(entity.cognitive.stress);
  const confidence = Math.round(entity.confidence);

  return (
    <header className="glass-bar relative z-20 flex h-11 shrink-0 items-center gap-3 border-b border-white/5 px-4 text-xs text-neutral-400">
      <div className="flex items-center gap-2">
        <img
          src="/eve-satoshi.png"
          alt="EVE"
          className="size-6 rounded-full object-cover ring-1 ring-amber-500/40"
        />
        <span className="font-semibold text-neutral-100">EVE</span>
      </div>
      <span className="text-neutral-700">·</span>
      <span className="flex items-center gap-1.5">
        <span className="size-1.5 animate-pulse rounded-full bg-emerald-400" />
        {entity.state} <span className="tabular text-neutral-200">{confidence}%</span>
      </span>
      <span className="hidden items-center gap-1.5 md:flex">
        <span className="text-neutral-600">·</span> Stress{' '}
        <span className="tabular text-amber-400">{stress}%</span>
      </span>

      <div className="flex-1" />

      <span className="hidden md:inline">
        Sync <span className="tabular text-neutral-300">{syncLabel}</span>
      </span>
      <span className="text-neutral-700">·</span>
      <span>
        {nextEvent.label}
        {nextEvent.seconds != null ? (
          <>
            {' in '}
            <span className="tabular text-amber-400">
              {formatScheduledCountdown(nextEvent.seconds)}
            </span>
          </>
        ) : (
          <span className="text-neutral-500"> — keine</span>
        )}
      </span>

      <MacroHeaderPopup />

      <CreditBadge />

      <button
        type="button"
        className="glass relative grid size-7 place-items-center rounded-lg text-neutral-400 transition hover:text-white"
        title="Benachrichtigungen"
      >
        <Bell className="size-3.5" />
        <span className="absolute top-1.5 right-1.5 size-1.5 rounded-full bg-amber-400" />
      </button>
    </header>
  );
}
